import { Component, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { TipoAllegato, Validazioni, BaseResponse, NazioniResponse, ConfigurazioneCaptcha, TipologieAllegato } from 'src/app/interfaces/utilities';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { LanguagesService } from 'src/app/services/languages.service';
import { ToastrService } from 'ngx-toastr';
import { RecordCompanyService } from 'src/app/services/record-company.service';
import { ValidationService } from 'src/app/services/validation.services';
import { map } from 'rxjs/operators';
import { checkResponse, checkFormValidity, showSuccessOrErrorToast, fileToBase64, substrBase64 } from 'src/app/utilities/utilities';
import { validator, errorMessage, getValidatorPattern, editionNominationsPerCategory } from 'src/app/utilities/validators';
import { CasaDiscografica, CasaDiscograficaRequest, Allegato } from 'src/app/interfaces/record-company';
import { Location } from '@angular/common';
import { MessageService } from 'src/app/services/message.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ReCaptcha2Component } from "ngx-captcha";
import { Observable, forkJoin } from 'rxjs';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { AttachmentsService } from 'src/app/services/attachments.service';

@Component({
	selector: 'app-record-company-registration',
	templateUrl: './record-company-registration.component.html',
	styles: []
})
export class RecordCompanyRegistrationComponent implements OnInit {

	options: FormlyFormOptions = {};
	LABEL_ATTACH = this.languageService.getValue('bc_attc_ed');
	model: any;
	form = new FormGroup({});
	listaNazioni = [];
	tipologie: TipoAllegato[]
	attachments: Allegato[];
	fields: FormlyFieldConfig[]
	validationList: Validazioni[];
	captchaConfiguration: ConfigurazioneCaptcha;

	private googlereCaptcha: ReCaptcha2Component;

	constructor(
		private languageService: LanguagesService,
		private toastrService: ToastrService,
		private recordCompanyService: RecordCompanyService,
		private location: Location,
		private validationsService: ValidationService,
		private messageService: MessageService,
		private configurationsService: ConfigurationService,
		private attachmentsService: AttachmentsService,
		private activeRoute: ActivatedRoute,
		private router: Router
	) { }

	ngOnInit() {
		this.messageService.reportMessage(this.languageService.getValue('record_company_registration'));
		this.captchaConfiguration = this.configurationsService.configuration.value;
		forkJoin(
			this.getNations()
		).subscribe((data) => {
			this.validationList = this.validationsService.validatorsList.value;
			this.listaNazioni = [];
			data[0].nazioni.forEach((nazione) => {
				this.listaNazioni.push({
					label: nazione.nome,
					value: nazione.id
				});
			});
			this.init();
		});

	}

	init(): void {
		this.model = {
			nome: '',
			siglaOriginale: '',
			sigla: '',
			indirizzo: '',
			telefono: '',
			telefonoAlternativo: '',
			citta: '',
			provincia: '',
			estero: false,
			idNazione: '',
			partitaIVA: '',
			numeroCanali: 1
		};

		this.getAttachmentsType().
			subscribe((tipologie: TipoAllegato[]) => {
				this.tipologie = tipologie;
			});

		this.createFields();
	}

	getAttachmentsType(): Observable<TipoAllegato[]> {
		return this.attachmentsService.getAttachmentsType().pipe(
			map((response: TipologieAllegato) => checkResponse(response, this.toastrService, "tipiAllegato"))
		)
	}

	createFields() {
		this.fields = [
			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						className: 'col-md-6',
						key: 'nome',
						type: 'sanremo-input-lite',
						templateOptions: {
							required: true,
							type: 'text',
							label: this.languageService.getValue('bc_form_nome_completo'),
							placeholder: this.languageService.getValue('bc_form_nome_completo'),
						},
						validation: {
							messages: {
								required: this.languageService.getValue("campo_obbligatorio")
							}
						}
					},
				]
			},

			{
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						className: 'col-md-6',
						key: 'sigla',
						type: 'sanremo-input-lite',
						templateOptions: {
							type: 'text',
							label: this.languageService.getValue('bc_form_acronimo'),
							placeholder: this.languageService.getValue('bc_form_acronimo'),
							required: true
						},
						validation: {
							messages: {
								required: this.languageService.getValue("campo_obbligatorio")
							}
						}
					},
					{
						className: 'col-6',
						key: 'telefono',
						type: 'input',
						templateOptions: {
							label: this.languageService.getValue('telefono_principale'),
							placeholder: this.languageService.getValue('telefono_principale'),
							required: true
						},
						validators: {
							cell: {
								expression: (c) => validator('cell', c.value, this.validationList),
								message: (error, field: FormlyFieldConfig) => errorMessage(field.formControl.value, 'cell', this.languageService)
							}
						},
						validation: {
							messages: {
								required: this.languageService.getValue("campo_obbligatorio")
							}
						}
					},
					{
						className: 'col-6',
						key: 'telefonoAlternativo',
						type: 'input',
						templateOptions: {
							label: this.languageService.getValue('telefono_alternativo'),
							placeholder: this.languageService.getValue('telefono_alternativo'),
						},
						validators: {
							cell: {
								expression: (c) => validator('cell', c.value, this.validationList),
								message: (error, field: FormlyFieldConfig) => errorMessage(field.formControl.value, 'cell', this.languageService)
							}
						},
					},
					{
						className: 'col-md-4',
						key: 'idNazione',
						type: 'select',
						templateOptions: {
							required: true,
							label: this.languageService.getValue('nazione_residenza_ufficio'),
							options: this.listaNazioni
						},
						validation: {
							messages: {
								required: this.languageService.getValue("campo_obbligatorio")
							}
						}
					},
					{
						className: 'col-2 mt-4',
						key: 'estero',
						type: 'sanremo-checkbox',
						defaultValue: false,
						templateOptions: {
							label: this.languageService.getValue('ed_pr_estero')
						},
						validators: {
							estero: {
								expression: c => {
									let partitaIVA = this.form.controls['partitaIVA'];
									let provincia = this.form.controls['provincia'];
									let regex: RegExp = getValidatorPattern('partita_IVA', this.validationList);

									if (c.value == false) {
										if (partitaIVA) {
											if (regex)
												partitaIVA.setValidators([Validators.pattern(regex), Validators.required]);
											else
												partitaIVA.setValidators([Validators.required]);

											partitaIVA.updateValueAndValidity();
										}

										if (provincia) {
											provincia.setValidators([Validators.required]);
											provincia.updateValueAndValidity();
										}
									}
									else {
										if (partitaIVA) {
											if (regex)
												partitaIVA.setValidators([Validators.pattern(regex)]);
											else
												partitaIVA.clearValidators();
											partitaIVA.updateValueAndValidity();
										}

										if (provincia) {
											provincia.clearValidators();
											provincia.updateValueAndValidity();
										}
									}
									return true;
								}
							}
						}
					},
					{
						className: 'col-md-6',
						key: 'indirizzo',
						type: 'sanremo-input-lite',
						templateOptions: {
							required: true,
							type: 'text',
							label: this.languageService.getValue('indirizzo'),
							placeholder: this.languageService.getValue('indirizzo'),
						},
						validation: {
							messages: {
								required: this.languageService.getValue("campo_obbligatorio")
							}
						}
					},
					{
						className: 'col-md-6',
						key: 'citta',
						type: 'sanremo-input-lite',
						templateOptions: {
							required: true,
							type: 'text',
							label: this.languageService.getValue('citta'),
							placeholder: this.languageService.getValue('citta'),
						},
						validation: {
							messages: {
								required: this.languageService.getValue("campo_obbligatorio")
							}
						}
					},
					{
						className: 'col-md-6',
						key: 'provincia',
						type: 'sanremo-input-lite',
						templateOptions: {
							type: 'text',
							label: this.languageService.getValue('provincia'),
							placeholder: this.languageService.getValue('provincia'),
						},
						validation: {
							messages: {
								required: this.languageService.getValue("campo_obbligatorio")
							}
						},
					},

					{
						className: 'col-6',
						key: 'partitaIVA',
						type: 'input',
						templateOptions: {
							label: this.languageService.getValue('partita_IVA'),
							placeholder: this.languageService.getValue('partita_IVA'),
						},
						modelOptions: {
							updateOn: 'blur'
						},
						validators: {
							partitaIVA: {
								expression: c => validator('partita_IVA', c.value, this.validationList),
								message: (error, field: FormlyFieldConfig) => errorMessage(field.formControl.value, 'partita_IVA', this.languageService)
							}
						},
						validation: {
							messages: {
								required: this.languageService.getValue("campo_obbligatorio"),
								pattern: this.languageService.getValue("invalid_VATnumber")
							}
						}
					},
				]
			},
			{
				fieldGroupClassName: "row",
				fieldGroup: [
					{
						className: "col-md-6",
						key: "logo",
						type: "file-input",
						templateOptions: {
							type: "file",
							label: this.languageService.getValue('logo'),
							required: false,
							callback: event => this.onDetectedFile(event, 0),
							fileName: null,
							attributes: { maxSize: 5, extraDesc: "max 5MB" }
						},
						wrappers: ["form-field", "file-wrapper-lite"],
						validation: {
							messages: {
								required: this.languageService.getValue('campo_obbligatorio')
							}
						}
					},
				]
			},
			{
				fieldGroupClassName: "row justify-content-end",
				fieldGroup: [
					{
						className: "col-md-12",

						key: "recaptcha",
						type: "sanremo-recaptcha",
						templateOptions: {
							siteKey: this.captchaConfiguration.siteKey,
							callback: (
								event: ReCaptcha2Component,
								valid: boolean
							) => {
								this.googlereCaptcha = event;
								this.model.ignoreCaptcha = !valid;
							}
						},
						expressionProperties: {
							"templateOptions.required": "!model.ignoreCaptcha"
						},
						validation: {
							messages: {
								required: this.languageService.getValue('campo_obbligatorio')
							}
						},
						wrappers: ["form-field"]
					}
				]
			}
		]
	}

	getNations(): Observable<NazioniResponse> {
		return this.recordCompanyService.getNations().pipe(
			map((response: NazioniResponse) => checkResponse(response, this.toastrService))
		)
	}

	reset() {
		this.options.resetModel();
		this.googlereCaptcha.reCaptchaApi.reset();
	}

	saveDraft() {
		let response = this.googlereCaptcha.getResponse();
		if (this.form.invalid) {
			checkFormValidity(this.form);
			this.toastrService.error('', this.languageService.getValue('errore_form'), {
				disableTimeOut: true
			});
			return;

		}
		this.model.bozza = true;
		let casaDiscografica: CasaDiscografica = { ...this.model };

		let request: CasaDiscograficaRequest = {
			casaDiscografica: casaDiscografica,
			allegati: this.attachments,
			captchaResponse: response
		};

		this.recordCompanyService.createRecordCompany(request).subscribe((response: BaseResponse) => {
			this.googlereCaptcha.reCaptchaApi.reset();
			this.form.get("recaptcha").reset();
			if (response && response.esito == 0) {
				this.options.resetModel();
				showSuccessOrErrorToast(response, 'casaDiscografica_creata', this.toastrService, this.languageService);
				setTimeout(() => {
					this.router.navigateByUrl("/home-page")
				}, 700);
			}
			else {
				this.toastrService.error(null, response.descrizione, {
					disableTimeOut: true
				});
			}
		})
	}

	onDetectedFile(event: any, counter) {
		if (event.target.files.length > 0) {

			if (!this.attachments || this.attachments == null) {
				this.attachments = new Array();
				this.attachments.push({
					nomeFile: '',
					tipoAllegato: "1",
					mediaTipoAllegato: '',
					base64Allegato: '',
					nomeAllegato: ''
				} as Allegato)
			}
			this.attachments[counter].nomeFile = event.target.files[0].name;
			this.attachments[counter].mediaTipoAllegato = event.target.files[0].type;
			this.attachments[counter].nomeAllegato = event.target.files[0].name.split(".")[0];
			fileToBase64(event.target.files[0]).then((result) => {
				this.attachments[counter].base64Allegato = substrBase64(result)
				this.model.casaDiscografica.allegati[counter].base64Allegato = substrBase64(result);
			});
			let attachmentName = (event.target.files[0].name as string).split(".")[0];
			this.attachments[counter].nomeAllegato = attachmentName;
		}
	}

}
